import { startListenMediaDevicesChange,
  stopListenMediaDevicesChange,
  mediaDevicesChange,
  selectAudioInput,
  buildSilenceAudio,
  silenceAudio,
  mediaDevicesQueryInProgress,
  mediaDevicesConstraintsChange,
  QUERY_MEDIA_DEVICES_TIMEOUT } from './../actions/media'
import { logger } from '../lib/utils/logger'

const buildSilenceAudioTrack = (store) => {
  const errNoWebAudioMsg = 'this browser does not implement WebAudio, audio will not work properly'
  const AudioContext = window.AudioContext || window.webkitAudioContext || false
  if (!AudioContext) {
    logger(errNoWebAudioMsg)
    return
  }
  // audio nodes
  const ctx = new AudioContext()
  if (!ctx.createMediaStreamDestination || !ctx.createOscillator) {
    logger(errNoWebAudioMsg)
    return
  }
  let dst = ctx.createMediaStreamDestination()
  const oscillator = ctx.createOscillator()
  oscillator.frequency.setValueAtTime(440, ctx.currentTime) // value in hertz
  // oscillator.start()

  oscillator.connect(dst)
  const track = dst.stream.getAudioTracks()[0]
  track.enabled = false

  store.dispatch(silenceAudio({
    context: ctx,
    stream: dst.stream,
    track,
  }))
}

const updateMediaInputDevicesHandler = async ({ store, constraints = {}, doneAction = null }) => {
  store.dispatch(mediaDevicesChange({
    listening: true,
  }))
  store.dispatch(mediaDevicesQueryInProgress(true))
  const startRequest = Date.now()
  const { mediaDevices: { constraints: stateConstraints, audioInput: { settings } } } = store.getState()

  // timeout to mark getUserMedia() call as a long one
  // (the user have to click a button in the confirm popup the browser displays)
  const timeout = setTimeout(() => {
    store.dispatch(mediaDevicesChange({
      queryDuration: Date.now() - startRequest,
    }))
  }, QUERY_MEDIA_DEVICES_TIMEOUT)

  if (!navigator.mediaDevices) {
    logger('warning: navigator.mediaDevices not found (available only on https)')
  }

  const audioInput = {
    stream: null,
    tracks: null,
    notAllowed: false,
    devices: null,
    error: false,
    settings,
  }
  const queryConstraints = { ...stateConstraints, ...constraints }
  try {
    audioInput.stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        ...queryConstraints,
        volume: 1.0,
      }
    })
    audioInput.tracks = await audioInput.stream.getAudioTracks()
    if (audioInput.tracks) {
      audioInput.settings = audioInput.tracks[0].getSettings()
      logger('=== track settings', audioInput.settings)
    }
  } catch (err) {
    audioInput.error = err
    audioInput.notAllowed = (err.name === 'NotAllowedError') // the user denied access to microphone
    logger('error on mediaDevices.getUserMedia', err)
  }

  if (navigator.mediaDevices) {
    audioInput.devices = await navigator.mediaDevices.enumerateDevices()
  }

  // the getUserMedia() call was fast, the answer was automatically
  // provided by the browser (the user already granted/denied access to microphone)
  clearTimeout(timeout)

  // save media devices
  store.dispatch(mediaDevicesChange({
    audioInput: { ...audioInput },
    constraints: { ...queryConstraints },
    queryDuration: Date.now() - startRequest,
    updated: Date.now(),
  }))

  // select the audio input track
  store.dispatch(selectAudioInput({
    stream: audioInput.stream,
    track: audioInput.tracks ? audioInput.tracks[0] : null,
  }))
  store.dispatch(mediaDevicesQueryInProgress(false))
  if (doneAction) {
    store.dispatch(doneAction)
  }
}

const listenMediaDevicesChangeOn = ({ store, doneAction = null }) => {
  if (!navigator.mediaDevices.ondevicechange) {
    logger('==== browser supported media constraints\n', navigator.mediaDevices.getSupportedConstraints())
    // this handler may be call with constraints by mediaDevicesConstraintsChange action (see below)
    const ondevicechangeHandler =
      ({ constraints = {}, doneAction = null } = {}) => updateMediaInputDevicesHandler({ store, constraints, doneAction })
    navigator.mediaDevices.ondevicechange = ondevicechangeHandler
    ondevicechangeHandler({ doneAction })
  }
}

const listenMediaDevicesChangeOff = (store) => {
  navigator.mediaDevices.ondevicechange = null
  logger('=== listenMediaDevicesChangeOff')
  store.dispatch(selectAudioInput(null))
  store.dispatch(mediaDevicesChange({
    queryDuration: 0,
    updated: 0,
    listening: false,
  }))
}

const onSelectAudioInput = (store) => {
  const { audio: { selectedAudioInput } } = store.getState()
  if (selectedAudioInput) {
    // stop previous retrieved tracks
    if (selectedAudioInput.track) {
      logger('   === media device change, stop previous tracks')
      selectedAudioInput.track.stop()
    }
    if (selectedAudioInput.stream) {
      selectedAudioInput.stream.getTracks().forEach((track) => track.stop())
    }
  }
}

export const mediaMiddleware = (store) => (next) => (action) => {
  const actionsHandlers = {
    [startListenMediaDevicesChange]: ({ doneAction = null } = {}) => listenMediaDevicesChangeOn({ store, doneAction }),
    [stopListenMediaDevicesChange]: () => listenMediaDevicesChangeOff(store),
    [buildSilenceAudio]: () => buildSilenceAudioTrack(store),
    [selectAudioInput]: () => onSelectAudioInput(store),
    [mediaDevicesConstraintsChange]: (constraints) => {
      // new constraints selected by user; retrieve track
      if (navigator.mediaDevices.ondevicechange) {
        navigator.mediaDevices.ondevicechange({ constraints })
      }
    },
  }
  if (actionsHandlers.hasOwnProperty(action.type)) {
    actionsHandlers[action.type](action.payload)
  }
  return next(action)
}
